import { prospectSourceWords, signalWords, stateWords } from '@/ui/words';
import { actionWords } from '@/oversight/auditWords';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { LOST_REASONS, prospectingOf, type LostReason } from '@norum/contracts';
import { failureOf, said } from '@/api/errors';
import { useAnswerIntro, useAskIntro, useEraseProspect, useFileObjection, useMarkLost, useOptOut, usePeople, useProfiles, useProspect, useSendSample, useWriteCompanyDetails } from '@/api/hooks';
import type { Relationship, WireProspect, WireProspectRecord } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { prospectFacts } from '@/outreach/Prospects';
import { STAGE_CHIP } from '@/outreach/words';
import { AgentBrief } from '@/ui/brief';
import { StackedScoreBar } from '@/ui/charts';
import { Select, Textarea } from '@/ui/fields';
import { Badge, Button, Field, Kicker, Skeleton, TextInput, STICK_FIRST, STACK_ON_PHONE } from '@/ui/primitives';
import { FactList, RecordHeader, when } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { ApproveCounterparty } from '@/directory/flows';
import { Banner } from '@/ui/status';
import { useToast } from '@/ui/Toast';
import { Plain, ToolLabel, useToolNames } from '@/app/technical';
import { Explainer } from '@/ui/context';
import { useWho } from '@/ui/who';
import { readerOf } from './explain';
import { explainProspect } from './explain-follow';
import { LOST_WORDS, productName, shortDate } from './words';
import { localIsoDate } from '@/today/dates';

/**
 * A prospect (S:14-prospect-ekstedt.png, S:15-prospect-sequence-
 * introduction-consent.png; ADR-0134, ADR-0136).
 *
 * Why this one and why now come first, because a person reads them before
 * anything is sent: the rules that earned its fit, against the profile's
 * bar, and the sentence written from the research. Then the warm path —
 * who already knows them — because an introduction goes before any cold
 * step. Every step of the cadence is a draft or a task a person acts on,
 * and a stop rule, when one fired, is said. Lost needs a reason; opting
 * them out is honoured by every plan; forgetting them runs through the
 * erasure flow, executed by a superadmin under a token.
 */

const STOP_WORDS: Record<string, string> = {
  replied: 'They replied — a person answers from here.',
  out_of_office: 'An out-of-office came back; the next step waits for their return.',
  bounced: 'The address bounced.',
  price_list_visit: 'They opened the price list.',
  lost: 'Marked lost.',
  silence: 'Thirty days of silence after the last step.',
  suppressed: 'They said stop.',
  erased: 'They asked to be forgotten.',
  introduced: 'Waiting on an introduction — the cold sequence starts only if it is declined.',
};


const STEP_WORDS = { email: 'Email', linkedin: 'LinkedIn', call: 'Call task in Today' } as const;

export function ProspectPage({ recordId }: { recordId?: string } = {}) {
  const names = useToolNames();
  const params = useParams();
  const id = recordId ?? params.id ?? '';
  const me = useMeOrThrow();
  const q = useProspect(id);
  const profiles = useProfiles(me.department);
  const who = useWho();
  if (q.isPending) return <Skeleton rows={12} />;
  if (q.isError) return <div className="p-6"><Refusal failure={failureOf(q.error)} /></div>;
  const { prospect: p, coldContact, record, steps = [] } = q.data;
  const r: WireProspectRecord | null = record;
  const profile = r?.plan ? profiles.data?.profiles.find((x) => x.id === r.plan!.profileId) : undefined;
  const score = r?.fitBreakdown.reduce((n, x) => n + x.points, 0) ?? p.fit?.score ?? null;

  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-8 p-6 phone:p-4" data-testid="prospect-page">
      <RecordHeader
        kicker="Prospect"
        title={p.businessName ?? p.domain ?? 'Name not written down yet'}
        subtitle={[p.zone, r?.plan?.territory].filter(Boolean).join(' · ') || p.marketCode}
        meta={score !== null ? `Fit ${score}` : null}
        chips={[STAGE_CHIP[p.stage], ...(p.foundAs && p.foundAs !== 'customer' ? [{ label: p.foundAs === 'supplier' ? 'Found as a supplier' : 'Found as a competitor', tone: 'neutral' as const }] : [])]}
        back={{ to: '/prospects', label: prospectingOf(me.department)?.found ?? 'Prospects' }}
        actions={r && !r.lost && !r.erasureOpen ? <Actions prospectId={p.id} products={profile?.products ?? []} outreach={prospectingOf(me.department)?.outreach ?? false} /> : undefined}
      />

      {r?.lost && (
        <Banner tone="danger" title={`Lost — ${LOST_WORDS[r.lost.reason as LostReason] ?? r.lost.reason}`}>
          Marked by {r.lost.by ?? 'a person'} {when(r.lost.at)}. Its cadence stopped, and it is counted against its plan.
          {r.lost.note ? ` “${r.lost.note}”` : ''}
        </Banner>
      )}
      {r?.erasureOpen && (
        <Banner tone="danger" title="They asked to be forgotten">
          Their address is suppressed and every cadence ended. A superadmin executes the erasure under a token bound to it.
        </Banner>
      )}

      <Explainer startOpen context={explainProspect(p, r, who, readerOf(me))} />

      {/* Find companies like this, from a prospect not yet filed as a company: its site is read, so what it met can seed a profile. */}
      {(me.isSuperadmin || me.leads.includes(me.department)) && p.stage !== 'discovered' && (
        <Link
          className="min-h-tap self-start text-dense text-accent underline underline-offset-2"
          to={`/prospecting/setup?market=${p.marketCode}&likeProspect=${p.id}`}
        >
          {p.foundAs === 'supplier' ? 'Find suppliers like this' : p.foundAs === 'competitor' ? 'Find more competitors like this' : 'Find companies like this'}
        </Link>
      )}

      <CompanyDetails prospect={p} />

      {r && (
        <Section label="Why this one, why now" note="Written from the research below. Read this first.">
          <FactList
            facts={[
              ['Why now', r.whyNow ?? <span key="none" className="text-secondary">No why-now sentence yet: it is written when the site has been read.</span>],
              ['In plan', r.plan ? <Link key="plan" className="text-accent underline" to={`/prospecting/plans/${r.plan.id}`}>{r.plan.segment} · {r.plan.territory}</Link> : 'None — found by hand or by a search, so no plan claims it'],
              // A lookalike's find says what it was found to look like (0130).
              ['Looks like', r.looksLike && r.looksLike.length > 0 ? r.looksLike.join(', ') : null],
              // Another plan's sweep met it too; this one keeps it (R13, 0136).
              [
                'Also met by',
                r.alsoMet && r.alsoMet.length > 0
                  ? r.alsoMet.map((m, i) => (
                      <span key={m.planId}>
                        {i > 0 ? ', ' : ''}
                        <Link className="text-accent underline" to={`/prospecting/plans/${m.planId}`}>{m.segment} · {m.territory}</Link>
                        {m.times > 1 ? ` (${m.times} times)` : ''}
                      </span>
                    ))
                  : null,
              ],
              ['Buys from today', r.incumbent ? `${r.incumbent} — the first email is written for that` : null],
            ]}
          />
          {r.fitBreakdown.length > 0 && (
            <StackedScoreBar segments={r.fitBreakdown.map((x) => ({ label: x.label, points: x.points }))} threshold={r.plan?.threshold ?? 0} />
          )}
        </Section>
      )}

      {r && <CompanyOf prospectId={p.id} company={r.company} domain={p.domain} department={me.department} />}

      {r && (r.firstEmail || r.priceTest) && (
        <Section label="When it goes" note="In the prospect’s own time, inside the market’s send window; a person approved it.">
          <FactList
            facts={[
              ['First email', r.firstEmail ? sendsWords(r.firstEmail) : null],
              ['Price test', r.priceTest ? `Arm ${r.priceTest.arm}: €${r.priceTest.eur} a kilo, quoted in the first email` : null],
            ]}
          />
        </Section>
      )}

      {r && <WarmPath prospectId={p.id} intros={r.intros} paths={r.warmPath} me={me.humanId} />}

      {r && <Cadence record={r} />}

      {r && (r.objection || r.cadence?.stopReason === 'replied') && <Objection prospectId={p.id} reading={r.objection} />}

      {r && r.samples.length > 0 && (
        <Section label="Samples">
          <FactList
            facts={r.samples.map((s): [string, ReactNode] => [
              `${productName(s.productCode)}${s.format ? ` · ${s.format}` : ''}${s.quantity ? ` · ${s.quantity}` : ''}`,
              <span key={s.id}>
                {stateWords(s.state)} · asked {when(s.requestedAt)}
                {s.outcomeAt ? ` · ${when(s.outcomeAt)}` : ''}
              </span>,
            ])}
          />
        </Section>
      )}

      {r && r.signals.length > 0 && (
        <Section label="Signals">
          <FactList
            facts={r.signals.map((s): [string, ReactNode] => [
              signalWords(s.kind),
              <span key={s.id} className="flex flex-col gap-0.5">
                <span>{s.said}</span>
                <span className="text-label text-meta">
                  +{s.points} · <SignalSource source={s.source} /> · {shortDate(s.seenOn)}
                </span>
              </span>,
            ])}
          />
        </Section>
      )}

      {r && (
        <Section label="Research trail" note="Each step that ran for it, the tool it called, what it came to, and what it cost at the prices Plans and usage records; below, the same steps as the audit chain holds them. A step that called nothing was answered from our own index.">
          {r.index && (
            <p className="m-0 text-dense text-secondary" data-testid="in-our-index">
              In our index since {shortDate(r.index.firstSeen)}, found by <ToolLabel id={r.index.sourceTool} />
              {r.index.hits > 1 ? ` · asked ${r.index.hits} times, so ${r.index.hits - 1} lookup${r.index.hits === 2 ? '' : 's'} cost nothing` : ''}
              {r.index.refreshedAt !== r.index.firstSeen ? ` · refreshed ${shortDate(r.index.refreshedAt)}` : ''}.
            </p>
          )}
          {steps.length > 0 && (
            <div className="overflow-x-auto border border-line">
              <table className={`w-full min-w-[640px] border-collapse ${STICK_FIRST}`} data-testid="research-steps">
                <thead>
                  <tr className="bg-raised">
                    {['When', 'Step', 'Tool', 'Result', 'State', 'Cost'].map((h) => (
                      <th key={h} scope="col" className="border-b border-line px-3 py-2.5 text-left text-micro font-medium uppercase tracking-label text-meta">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {steps.map((st) => {
                    const calls = Object.entries(st.calls);
                    const result = stepResult(st.step, p, r);
                    return (
                      <tr key={`${st.step}-${st.at}`} className="border-b border-line last:border-b-0">
                        <td className="px-3 py-2.5 text-dense">{shortWhen(st.at)}</td>
                        <td className="px-3 py-2.5 text-dense">{STEP_NAMES[st.step] ?? st.step.replace(/_/g, ' ')}</td>
                        <td className="px-3 py-2.5 text-dense">{calls.length ? calls.map(([t, n]) => `${names([t])} × ${n}`).join(', ') : <span className="text-meta">none · from our index</span>}</td>
                        <td className="px-3 py-2.5 text-dense">{result.said}</td>
                        <td className="px-3 py-2.5 text-dense"><Badge tone={result.tone}>{result.state}</Badge></td>
                        <td className="px-3 py-2.5 text-dense tabular-nums">{st.eur !== null ? `€${st.eur.toFixed(4)}` : calls.length === 0 ? '€0' : <span className="text-meta">no price for {names(st.unpriced)}</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {r.trail.length === 0 ? (
            <p className="m-0 text-dense text-meta">Nothing has run for it yet.</p>
          ) : (
            <FactList
              facts={r.trail.map((t): [string, ReactNode] => [
                shortWhen(t.at),
                <span key={`${t.action}-${t.at}`}>
                  {TRAIL_WORDS[t.action] ?? actionWords(t.action)}
                  {t.outcome !== 'ok' ? <span className="text-danger"> · {stateWords(t.outcome).toLowerCase()}</span> : null}
                </span>,
              ])}
            />
          )}
        </Section>
      )}

      <Section label="What we know of it">
        <FactList facts={prospectFacts(p, coldContact)} />
        {p.googleUrl && (
          <a className="text-dense text-accent underline" href={p.googleUrl} target="_blank" rel="noreferrer noopener">
            See it on Google Maps
          </a>
        )}
      </Section>

      <Section label="Consent" note="One opt-out is honoured by every plan; deletion runs through the erasure flow.">
        <FactList
          facts={[
            ['Basis', coldContact === 'open' ? 'The markets file’s legal footing for this country, confirmed by a person' : coldContact],
            ['Opted out', p.exclusion === 'suppressed' ? <span key="o" className="text-danger">Yes — they said stop{p.excludedAt ? ` ${when(p.excludedAt)}` : ''}. Their address is on the list every plan honours, as a hash.</span> : r?.erasureOpen ? 'They asked to be forgotten; the erasure is open' : 'No'],
            ['What we hold', whatWeHold(p, r).join(' · ')],
            ['Where it came from', prospectSourceWords(r ? r.source : p.source)],
          ]}
        />
      </Section>

      <AgentBrief
        lines={[
          ['prospect', p.id],
          ['market', p.marketCode],
          ['stage', p.stage],
          ['plan', r?.plan?.id],
          ['looks_like', r?.looksLike?.length ? r.looksLike.join('; ') : null],
          ['profile', r?.plan?.profileId],
          ['fit', score],
          ['threshold', r?.plan?.threshold],
          ['cadence', r?.cadence ? `${r.cadence.state}${r.cadence.stopReason ? `:${r.cadence.stopReason}` : ''}` : null],
          ['intros', r?.intros.length || null],
          ['samples', r?.samples.length || null],
          ['objection', r?.objection?.objection],
          ['lost', r?.lost?.reason],
          ['warm_path', r?.warmPath.length || null],
          ['incumbent', r?.incumbent],
          ['in_index', r?.index ? 'yes' : null],
          ['opted_out', p.exclusion === 'suppressed' ? 'yes' : null],
        ]}
      />
    </div>
  );
}

/** Which step a receipt is, and the tools that step uses (F5). A receipt keeps no payload, so this is the step's own. */
const TRAIL_WORDS: Record<string, string> = {
  'prospect.discovered': 'Found by a Places search (ids only)',
  'prospect.read': 'Read: Place Details live, then its own site through the scanner, scored by the rubric',
  'prospect.excluded': 'Excluded, for the reason on the record',
  'prospect.enriched': 'An address found: the register, its contact page, or Hunter — checked and sealed',
  'prospect.drafted': 'A first email drafted on Vertex, into the shared box for a person',
  'prospect.watched': 'Its site read again a month on',
};

const STEP_NAMES: Record<string, string> = {
  sweep_plan: 'Found by its plan’s sweep',
  discover_places: 'Found by a search',
  read_site: 'Read',
  watch_site: 'Read again',
  enrich_contact: 'Address found',
  draft_first_touch: 'First email drafted',
};

const shortWhen = (iso: string) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/**
 * What a step came to, read from the record it left rather than from the
 * step row (which keeps identifiers and counts, never what came back):
 * a site read leaves a domain and a score, an enrichment an address, a
 * draft a thread.
 */
function stepResult(step: string, p: WireProspect, r: WireProspectRecord): { said: string; state: string; tone: 'accent' | 'neutral' | 'warning' | 'danger' } {
  switch (step) {
    case 'sweep_plan':
    case 'discover_places':
      return { said: p.placeId ? 'A place, by its id' : 'A name and a site', state: 'found', tone: 'neutral' };
    case 'read_site':
    case 'watch_site':
      return p.domain
        ? { said: `${p.domain} read${p.fit ? `, scored ${p.fit.score}` : ''}${r.whyNow ? ', why-now written' : ''}`, state: p.fit?.tier === 'weak' || p.fit?.tier === 'excluded' ? 'below the bar' : 'read', tone: p.fit?.tier === 'weak' || p.fit?.tier === 'excluded' ? 'warning' : 'accent' }
        : { said: 'No site to read', state: 'not found', tone: 'warning' };
    case 'enrich_contact':
      return p.hasAddress
        ? { said: `${p.addressKind === 'role' ? 'A shared' : 'A person’s'} address, ${p.verification ?? 'unchecked'}${p.register ? ` · ${p.register.legalForm}` : ''}`, state: 'sealed', tone: 'accent' }
        : { said: 'No address found — written as not found, never guessed', state: 'not found', tone: 'warning' };
    case 'draft_first_touch':
      return p.threadId ? { said: 'A draft in the shared box, for a person', state: 'drafted', tone: 'accent' } : { said: 'No draft left', state: 'refused', tone: 'danger' };
    default:
      return { said: '—', state: 'ran', tone: 'neutral' };
  }
}

/** What we hold about them, in words: never a value, only which kinds (R15). */
function whatWeHold(p: WireProspect, r: WireProspectRecord | null): string[] {
  const held = ['the business’s name and country'];
  if (p.domain) held.push('its website address');
  if (p.placeId) held.push('Google’s id for the place (nothing Google said)');
  if (p.hasAddress) held.push(`one ${p.addressKind === 'role' ? 'shared' : 'personal'} email address, sealed under its own key`);
  if (p.register) held.push('what the national register says of the company');
  if (r?.index) held.push('a row in our index');
  if (r && r.signals.length > 0) held.push(`${r.signals.length} signal${r.signals.length === 1 ? '' : 's'} read off public pages`);
  return held;
}

/** "Sends Tue 29 Sep, 09:40 Stockholm time": in the market's own zone (R14). */
function sendsWords(f: NonNullable<WireProspectRecord['firstEmail']>): string {
  const zone = f.zone ?? 'UTC';
  const at = new Date(f.sentAt ?? f.scheduledFor);
  const words = new Intl.DateTimeFormat('en-GB', { timeZone: zone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(at);
  const place = zone.split('/').pop()!.replace(/_/g, ' ');
  return `${f.sentAt ? 'Left' : 'Sends'} ${words} ${place} time`;
}

/**
 * Its company (PR-15): the core row with its own domain, or none yet.
 * Approving it is a person's, under a token bound to this prospect; core-api
 * files the company if it is not there, and approves it as what this
 * department approves one as.
 */
function CompanyOf({ prospectId, company, domain, department }: { prospectId: string; company: WireProspectRecord['company']; domain: string | null; department: string }) {
  const [open, setOpen] = useState(false);
  const approveAs = prospectingOf(department)?.approveAs ?? 'client';
  return (
    <Section label="Its company" note="Being found is not being approved: nothing writes to a company until a person says what it is to us.">
      <FactList
        facts={[
          ['Company', company ? <Link key="c" className="text-accent underline" to={`/companies/${company.id}`}>{company.name}</Link> : domain ? `Not in our records yet — ${domain}` : 'Not in our records yet — write its website down above to file it'],
          ['Relationship', company ? (company.relationship === 'unapproved' ? 'Unapproved' : company.relationship) : null],
        ]}
      />
      {domain && (!company || company.relationship === 'unapproved') && (
        <Button onClick={() => setOpen(true)} className="self-start">
          {company ? `Approve as ${approveAs}` : `File and approve as ${approveAs}`}
        </Button>
      )}
      {open && (
        <ApproveCounterparty
          kind="company"
          id={company?.id ?? ''}
          name={company?.name ?? domain ?? 'The company'}
          current={(company?.relationship as Relationship | undefined) ?? 'unapproved'}
          prospectId={prospectId}
          preset={approveAs}
          onClose={() => setOpen(false)}
        />
      )}
    </Section>
  );
}

/** A chef move names the contact it rests on by id: their record, where the old relationship is (R3). */
function SignalSource({ source }: { source: string | null }) {
  const contact = source?.match(/^contact:([0-9a-f-]{36})$/)?.[1];
  if (contact) return <Link className="text-accent underline" to={`/contacts/${contact}`}>the contact we know</Link>;
  return <>{source ?? 'no source'}</>;
}

/**
 * What their reply objected to (R9). The list reads a reply when it arrives
 * and keeps only its own phrase; a person files what it could not read, or
 * corrects it, and the correction is a new reading, never an edit.
 */
function Objection({ prospectId, reading }: { prospectId: string; reading: WireProspectRecord['objection'] }) {
  const file = useFileObjection();
  const toast = useToast();
  const [choice, setChoice] = useState<LostReason>((reading?.objection as LostReason | undefined) ?? 'other');
  const how = !reading
    ? 'They replied, and the list recognised no objection in it. Read the thread and file one if there is.'
    : reading.byPerson
      ? `Filed by a person ${when(reading.classifiedAt)}.`
      : `Read from their reply ${when(reading.classifiedAt)}, by the phrase “${reading.cue ?? ''}”.`;
  return (
    <Section label="Their reply" note="Counted per plan and market in the objection library. Lost takes the same reasons.">
      <FactList facts={[['Objection', reading ? LOST_WORDS[reading.objection as LostReason] ?? reading.objection : 'None read'], ['How', how]]} />
      <div className="flex flex-wrap items-end gap-2">
        <Field label={reading ? 'Correct it' : 'File one'}>
          <Select aria-label="Objection" value={choice} onChange={(e) => setChoice(e.target.value as LostReason)} options={LOST_REASONS.map((x) => ({ value: x, label: LOST_WORDS[x] }))} />
        </Field>
        <Button
          disabled={file.isPending || choice === reading?.objection}
          onClick={() => file.mutate({ prospectId, objection: choice }, { onSuccess: () => toast({ text: 'Filed. The earlier reading is kept.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}
        >
          File
        </Button>
      </div>
    </Section>
  );
}

const SOURCE_WORDS = { person: 'Written down by', own_site: 'From its own website', register: 'From its national register', research: 'From research' } as const;

/**
 * The company's details (0126): its name, address, phone, website and
 * number, as a person wrote them down from the screen — its site, a
 * register, Google Maps in their own browser — or as its own site gave
 * them. Nothing here was copied out of Google's API by software.
 */
function CompanyDetails({ prospect: p }: { prospect: WireProspect }) {
  const d = p.details;
  const [editing, setEditing] = useState(false);
  const empty = !p.businessName && !d?.postalAddress && !d?.phone && !d?.website && !p.domain;
  const source = d?.source
    ? d.source === 'person'
      ? `${SOURCE_WORDS.person} ${d.by ?? 'a colleague'}${d.at ? `, ${when(d.at)}` : ''}`
      : `${SOURCE_WORDS[d.source]}${d.at ? `, ${when(d.at)}` : ''}`
    : null;
  const site = d?.website ?? (p.domain ? `https://${p.domain}/` : null);
  return (
    <Section label="Company details" note="Written down from the screen: its own site, a register, or Google Maps opened in your browser.">
      {empty && !editing && (
        <p className="m-0 text-body" data-testid="details-empty">
          Nobody has written this company’s details down yet. Otis reads its own website when he gets to it; you can write them down now from its listing.
        </p>
      )}
      {!editing && (
        <FactList
          facts={[
            ['Name', p.businessName],
            ['Address', d?.postalAddress ?? null],
            ['Phone', d?.phone ?? null],
            ['Website', site ? <a key="w" className="text-accent underline" href={site} target="_blank" rel="noreferrer noopener">{site.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a> : null],
            ['Org number', d?.orgNumber ?? p.register?.registrationId ?? null],
            ['Note', d?.note ?? null],
            ['Source', d?.sourceUrl ? <a key="s" className="text-accent underline" href={d.sourceUrl} target="_blank" rel="noreferrer noopener">{source}</a> : source],
          ]}
        />
      )}
      {editing ? (
        <DetailsForm prospect={p} onDone={() => setEditing(false)} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={() => setEditing(true)} data-testid="details-edit">
            {empty ? 'Write details down' : 'Edit details'}
          </Button>
          {p.googleUrl && (
            <a className="text-dense text-accent underline" href={p.googleUrl} target="_blank" rel="noreferrer noopener">
              Open it on Google Maps to read them
            </a>
          )}
        </div>
      )}
    </Section>
  );
}

function DetailsForm({ prospect: p, onDone }: { prospect: WireProspect; onDone: () => void }) {
  const write = useWriteCompanyDetails();
  const toast = useToast();
  const d = p.details;
  const [v, setV] = useState({
    businessName: p.businessName ?? '',
    postalAddress: d?.postalAddress ?? '',
    phone: d?.phone ?? '',
    website: d?.website ?? (p.domain ? `https://${p.domain}/` : ''),
    orgNumber: d?.orgNumber ?? p.register?.registrationId ?? '',
    note: d?.note ?? '',
  });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((x) => ({ ...x, [k]: e.target.value }));
  const changed = Object.fromEntries(Object.entries(v).filter(([, x]) => x.trim() !== '')) as Partial<typeof v>;
  return (
    <form
      className="flex flex-col gap-3"
      data-testid="details-form"
      onSubmit={(e) => {
        e.preventDefault();
        write.mutate(
          { prospectId: p.id, ...changed },
          {
            onSuccess: () => {
              toast({ text: 'Written down. It is this company’s record now.' });
              onDone();
            },
            onError: (err) => toast({ text: said(failureOf(err)), tone: 'danger' }),
          },
        );
      }}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name"><TextInput value={v.businessName} onChange={set('businessName')} placeholder="As the company writes it" /></Field>
        <Field label="Website"><TextInput value={v.website} onChange={set('website')} placeholder="https://" /></Field>
        <Field label="Address"><TextInput value={v.postalAddress} onChange={set('postalAddress')} placeholder="Street, postcode, city" /></Field>
        <Field label="Phone"><TextInput value={v.phone} onChange={set('phone')} placeholder="+46 8 …" /></Field>
        <Field label="Org number"><TextInput value={v.orgNumber} onChange={set('orgNumber')} placeholder="From its register" /></Field>
        <Field label="Note"><TextInput value={v.note} onChange={set('note')} placeholder="Anything worth knowing" /></Field>
      </div>
      {write.isError && <Refusal failure={failureOf(write.error)} compact />}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={write.isPending || Object.keys(changed).length === 0}>{write.isPending ? 'Saving…' : 'Save'}</Button>
        <Button icon="close" type="button" variant="quiet" onClick={onDone}>Cancel</Button>
        {p.googleUrl && (
          <a className="self-center text-dense text-accent underline" href={p.googleUrl} target="_blank" rel="noreferrer noopener">
            Open it on Google Maps
          </a>
        )}
      </div>
    </form>
  );
}

function Section({ label, note, children }: { label: string; note?: string | undefined; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5" aria-label={label}>
      <Kicker>{label}</Kicker>
      {note && <p className="m-0 max-w-[64ch] text-dense text-meta"><Plain>{note}</Plain></p>}
      {children}
    </section>
  );
}

type Open = null | 'sample' | 'lost' | 'optout' | 'erase';

/**
 * Send a sample, mark lost, opt them out, forget them (S:14). Each says what
 * it does before it does it. A sample and an opt-out are outreach's; where
 * nobody is written to (PR-23), a record is only lost or forgotten.
 */
function Actions({ prospectId, products, outreach }: { prospectId: string; products: string[]; outreach: boolean }) {
  const [open, setOpen] = useState<Open>(null);
  const sample = useSendSample();
  const lost = useMarkLost();
  const optOut = useOptOut();
  const erase = useEraseProspect();
  const toast = useToast();
  const [product, setProduct] = useState(products[0] ?? '');
  const [format, setFormat] = useState('');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState<LostReason>('price');
  const [lostNote, setLostNote] = useState('');
  const [optOutReason, setOptOutReason] = useState<'objected' | 'manual'>('objected');
  const [receivedOn, setReceivedOn] = useState(localIsoDate(new Date()));
  const fail = (e: unknown) => toast({ text: said(failureOf(e)), tone: 'danger' });
  const done = (text: string) => {
    setOpen(null);
    toast({ text });
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div className={`flex flex-wrap gap-2 ${STACK_ON_PHONE}`}>
        {outreach && <Button icon="forward" variant="primary" onClick={() => setOpen(open === 'sample' ? null : 'sample')}>Send a sample</Button>}
        <Button onClick={() => setOpen(open === 'lost' ? null : 'lost')}>Mark lost</Button>
        {outreach && <Button onClick={() => setOpen(open === 'optout' ? null : 'optout')}>Opt them out</Button>}
        <Button variant="danger" onClick={() => setOpen(open === 'erase' ? null : 'erase')}>Delete everything about them</Button>
      </div>

      {open === 'sample' && (
        <form
          className="flex flex-col gap-3 border border-strong p-4"
          onSubmit={(e) => {
            e.preventDefault();
            sample.mutate(
              { prospectId, product, ...(format.trim() ? { format: format.trim() } : {}), ...(quantity.trim() ? { quantity: quantity.trim() } : {}), ...(note.trim() ? { note: note.trim() } : {}) },
              {
                onSuccess: (r) =>
                  done(
                    r.existing
                      ? 'This sample was asked of Production already and is not sent yet, so it is not asked twice.'
                      : r.routing?.outcome === 'refused'
                        ? `Production cannot take it: ${r.routing.reason}`
                        : 'Asked of Production. A call about it is on your day a week from now.',
                  ),
                onError: fail,
              },
            );
          }}
        >
          <p className="m-0 text-dense text-secondary">A request to Production, which sends it; and a call task on your day for a week later, in their country’s hours.</p>
          {products.length > 0 ? (
            <Field label="Product">
              <Select aria-label="Product" value={product} onChange={(e) => setProduct(e.target.value)} options={products.map((x) => ({ value: x, label: productName(x) }))} />
            </Field>
          ) : (
            <Field label="Product" hint="Its code, as the price list names it — e.g. black_garlic.">
              <TextInput aria-label="Product" value={product} onChange={(e) => setProduct(e.target.value)} />
            </Field>
          )}
          <Field label="Format" hint="Optional: the pack, as the price list names it.">
            <TextInput aria-label="Format" value={format} onChange={(e) => setFormat(e.target.value)} />
          </Field>
          <Field label="Quantity" hint="Optional: how much, e.g. 2 × 1 kg glass. Production reads it on the request.">
            <TextInput aria-label="Quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} maxLength={80} />
          </Field>
          <Field label="A note for Production" hint="Optional.">
            <Textarea aria-label="A note for Production" value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[72px] w-full" />
          </Field>
          <div>
            <Button icon="ask" type="submit" variant="primary" disabled={sample.isPending || !/^[a-z][a-z_]*$/.test(product)}>Ask Production</Button>
          </div>
        </form>
      )}

      {open === 'lost' && (
        <div className="flex flex-wrap items-end gap-2 border border-strong p-4">
          <Field label="Why it was lost">
            <Select aria-label="Why it was lost" value={reason} onChange={(e) => setReason(e.target.value as LostReason)} options={LOST_REASONS.map((x) => ({ value: x, label: LOST_WORDS[x] }))} />
          </Field>
          <div className="min-w-[240px] flex-1">
            <Field label="In your words" hint="Optional. Read by whoever opens it next, and by the profile’s learning.">
              <TextInput aria-label="In your words" value={lostNote} onChange={(e) => setLostNote(e.target.value)} maxLength={500} />
            </Field>
          </div>
          <Button
            variant="danger"
            disabled={lost.isPending}
            onClick={() => lost.mutate({ prospectId, reason, ...(lostNote.trim() ? { note: lostNote.trim() } : {}) }, { onSuccess: () => done('Marked lost. Its cadence stopped.'), onError: fail })}
          >
            Mark it lost
          </Button>
        </div>
      )}

      {open === 'optout' && (
        <div className="flex flex-wrap items-end gap-3 border border-strong p-4">
          <span className="basis-full text-dense text-secondary">Their address goes on the list of people who said stop, for every plan, and every cadence ends. Only a superadmin lifts it.</span>
          <Field label="Why">
            <Select
              aria-label="Why they are opted out"
              value={optOutReason}
              onChange={(e) => setOptOutReason(e.target.value as 'objected')}
              options={[
                { value: 'objected', label: 'They asked us to stop' },
                { value: 'manual', label: 'We decided not to write to them' },
              ]}
            />
          </Field>
          <Button variant="danger" disabled={optOut.isPending} onClick={() => optOut.mutate({ prospectId, reason: optOutReason }, { onSuccess: () => done('Opted out. Nobody here writes to them again.'), onError: fail })}>
            Opt them out
          </Button>
        </div>
      )}

      {open === 'erase' && (
        <div className="flex flex-wrap items-end gap-3 border border-danger p-4">
          <span className="basis-full text-dense text-secondary">
            Recorded now, and their address suppressed at once. A superadmin executes it under a token bound to the request; what only they were on loses its key.
          </span>
          <Field label="When they asked">
            <TextInput aria-label="When they asked" type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
          </Field>
          <Button
            variant="danger"
            disabled={erase.isPending || !receivedOn}
            onClick={() => erase.mutate({ prospectId, receivedOn }, { onSuccess: () => done('Recorded. The erasure waits for a superadmin, and their address is suppressed now.'), onError: fail })}
          >
            Record the request
          </Button>
        </div>
      )}
    </div>
  );
}

/** Before any cold message, who already knows them (S:14-15): an introduction is asked of a colleague, and answered by them only. */
const STRENGTH: Record<WireProspectRecord['warmPath'][number]['strength'], { label: string; tone: 'accent' | 'neutral' | 'warning' }> = {
  strong: { label: 'STRONG', tone: 'accent' },
  medium: { label: 'MEDIUM', tone: 'neutral' },
  weak: { label: 'WEAK', tone: 'warning' },
};

function WarmPath({ prospectId, intros, paths, me }: { prospectId: string; intros: WireProspectRecord['intros']; paths: WireProspectRecord['warmPath']; me: string }) {
  const people = usePeople();
  const ask = useAskIntro();
  const answer = useAnswerIntro();
  const toast = useToast();
  const [via, setVia] = useState('');
  const [message, setMessage] = useState('');
  const [outcome, setOutcome] = useState('');
  // Asked of someone in this department, who can see the prospect and answer.
  const department = useMeOrThrow().department;
  const colleagues = (people.data?.people ?? []).filter((x) => x.state === 'here' && x.id !== me && (x.departments.includes(department) || x.leads.includes(department)));
  const myself = people.data?.people.find((x) => x.id === me);
  const chosen = via || colleagues[0]?.email || '';
  const open = intros.find((i) => i.state === 'asked');
  const HOW = { works_there: 'works there today', worked_there: 'worked there once' } as const;
  return (
    <Section label="Warm path first" note="Before any cold message, who already knows them: the people the core holds at its company, and the colleague asked to introduce us. A warm path turns the first step into an introduction request.">
      {paths.length > 0 ? (
        <ul className="m-0 flex list-none flex-col gap-1.5 border border-line p-0" data-testid="warm-paths">
          {paths.map((w) => (
            <li key={w.contactId} className="flex flex-wrap items-center gap-2 px-4 py-2 text-dense">
              <Link className="text-accent underline" to={`/contacts/${w.contactId}`}>{w.who}</Link>
              <span className="text-secondary">
                {HOW[w.how]}
                {w.roleTitle ? ` as ${w.roleTitle}` : ''}
              </span>
              <Badge tone={STRENGTH[w.strength].tone}>{STRENGTH[w.strength].label}</Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="m-0 text-dense text-secondary">Nobody the core knows works or worked there. The way in is cold unless a colleague knows them.</p>
      )}
      {intros.length > 0 && (
        <FactList
          facts={intros.map((i): [string, ReactNode] => [
            stateWords(i.state),
            <span key={i.id} className="flex flex-col gap-1">
              <span>
                Asked of {i.via} {when(i.askedAt)}
                {i.answeredAt ? ` · ${stateWords(i.state).toLowerCase()} ${when(i.answeredAt)}` : ''}
              </span>
              {i.message && <span className="text-secondary">“{i.message}”</span>}
              {i.outcome && <span className="text-secondary">{i.via}: “{i.outcome}”</span>}
              {i.state === 'asked' && myself && i.via === myself.displayName && (
                <span className="flex flex-col gap-2">
                  <Field label="What came of it" hint="Optional, in your words.">
                    <TextInput aria-label="What came of it" value={outcome} onChange={(e) => setOutcome(e.target.value)} maxLength={500} />
                  </Field>
                  <span className="flex flex-wrap gap-2">
                    <Button size="row" variant="primary" disabled={answer.isPending} onClick={() => answer.mutate({ introId: i.id, made: true, ...(outcome.trim() ? { outcome: outcome.trim() } : {}) }, { onSuccess: () => toast({ text: 'Made. The cold sequence does not start.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}>
                      I introduced them
                    </Button>
                    <Button size="row" disabled={answer.isPending} onClick={() => answer.mutate({ introId: i.id, made: false, ...(outcome.trim() ? { outcome: outcome.trim() } : {}) }, { onSuccess: () => toast({ text: 'Declined. The cold sequence starts in its next window.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) })}>
                      I can’t
                    </Button>
                  </span>
                </span>
              )}
            </span>,
          ])}
        />
      )}
      {!open && colleagues.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Ask a colleague who knows them">
              <Select aria-label="Ask a colleague who knows them" value={chosen} onChange={(e) => setVia(e.target.value)} options={colleagues.map((c) => ({ value: c.email, label: c.displayName }))} />
            </Field>
            <Button icon="ask"
              disabled={ask.isPending || !chosen}
              onClick={() =>
                ask.mutate(
                  { prospectId, via: chosen, ...(message.trim() ? { message: message.trim() } : {}) },
                  { onSuccess: () => toast({ text: 'Asked. It is on their day, and the cold sequence waits for the answer.' }), onError: (e) => toast({ text: said(failureOf(e)), tone: 'danger' }) },
                )
              }
            >Ask for an introduction</Button>
          </div>
          <Field label="What to say" hint="Optional: what you would like them to pass on. It goes with the ask, on their day; nothing is sent to the prospect.">
            <Textarea aria-label="What to say" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000} className="min-h-[64px] w-full" />
          </Field>
        </div>
      )}
    </Section>
  );
}

/** The cadence (S:15): each step a draft or a task, and the stop rule when one fired. */
function Cadence({ record }: { record: WireProspectRecord }) {
  const navigate = useNavigate();
  const c = record.cadence;
  const gaps = c?.gaps.split('/') ?? [];
  const zone = record.zone ?? record.firstEmail?.zone ?? null;
  const window = (iso: string | null | undefined): string => {
    if (!iso || !zone) return '—';
    const words = new Intl.DateTimeFormat('en-GB', { timeZone: zone, weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(iso));
    return `${words} ${zone.split('/').pop()!.replace(/_/g, ' ')} time`;
  };
  const stateOf = (step: 'email' | 'linkedin' | 'call'): { state: string; when: string; window: string } => {
    const made = c?.steps.find((s) => s.step === step);
    if (made) return { state: made.step === 'email' ? 'drafted' : 'on Today', when: when(made.dueAt), window: window(step === 'email' ? (record.firstEmail?.sentAt ?? record.firstEmail?.scheduledFor ?? made.dueAt) : made.dueAt) };
    if (!c) return { state: 'not started', when: '—', window: '—' };
    if (c.state === 'stopped') return { state: 'stopped', when: '—', window: '—' };
    if (c.nextStep === step) return { state: 'next', when: c.nextAt ? when(c.nextAt) : '—', window: window(c.nextAt) };
    return { state: 'waiting', when: '—', window: '—' };
  };
  return (
    <Section
      label="Outreach sequence"
      note={`Email → LinkedIn → a call task in Today${gaps.length === 2 ? `, gaps ${gaps[0]} and ${gaps[1]} days` : ''}. Each step is a draft or a task a person acts on; any stop rule ends it.${record.incumbent ? ` They buy from ${record.incumbent} today, and the email is written for that.` : ''}`}
    >
      <div className="overflow-x-auto border border-line">
        <table className={`w-full min-w-[560px] border-collapse ${STICK_FIRST}`} data-testid="cadence">
          <thead>
            <tr className="bg-raised">
              {['Step', 'State', 'When', 'Send window'].map((h) => (
                <th key={h} scope="col" className="border-b border-line px-3 py-2.5 text-left text-micro font-medium uppercase tracking-label text-meta">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(['email', 'linkedin', 'call'] as const).map((step) => {
              const s = stateOf(step);
              return (
                <tr key={step} className="border-b border-line last:border-b-0">
                  <td className="px-3 py-2.5 text-dense font-semibold">{STEP_WORDS[step]}</td>
                  <td className="px-3 py-2.5 text-dense text-secondary">{s.state}</td>
                  <td className="px-3 py-2.5 text-dense text-secondary">{s.when}</td>
                  <td className="px-3 py-2.5 text-dense text-secondary">{s.window}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {c?.state === 'stopped' && c.stopReason && (
        <p className="m-0 text-dense text-danger" data-testid="cadence-stopped">
          Stopped: {STOP_WORDS[c.stopReason] ?? c.stopReason.replace(/_/g, ' ')}
        </p>
      )}
      {!c && <p className="m-0 text-dense text-secondary">No sequence yet: it starts when the first email has left.</p>}
      {c?.steps.some((s) => s.taskId) && (
        <div>
          <Button size="row" onClick={() => void navigate('/today')}>Its tasks are on Today</Button>
        </div>
      )}
    </Section>
  );
}
